import type { OrganizationProfile, OrgProfileUpdate } from '@hero/schema';
import type { FinancingFieldBadgeVariant } from '../../../app/FinancingFieldBadge';

/**
 * Context the form is being rendered in. Drives which fields get a
 * financing chip and (in the future) which Zod resolver tightens
 * required-ness.
 *
 * - `'profile'`   — the carrier-dashboard profile tabs, where every
 *                   field is optional and saved independently.
 * - `'financing'` — the financing apply gate, where the lender
 *                   needs the hard fields to underwrite. Renders
 *                   the red `required` chip on hard fields and the
 *                   green `recommended` chip on soft ones.
 */
export type OrgProfileSectionContext = 'profile' | 'financing';

/**
 * Per-field financing-chip resolver — derives the right chip
 * variant from the context. Pulled out so the three section forms
 * share the same lookup logic without copy-pasting the `if (ctx ==
 * financing) return 'required'` boilerplate.
 */
export type FinancingChipFor = (
  variant: 'hard' | 'soft',
) => FinancingFieldBadgeVariant | undefined;

/**
 * Compose a `chip(...)` helper bound to a given `displayFor`
 * context. In the financing context we surface hard fields as
 * `required` (red) and soft fields as `recommended` (green); in
 * the profile context we render no chip at all.
 */
export function makeChipFor(displayFor: OrgProfileSectionContext): FinancingChipFor {
  if (displayFor !== 'financing') return () => undefined;
  return variant => (variant === 'hard' ? 'required' : 'recommended');
}

/**
 * Section save callback. Each form owns its own RHF instance and
 * passes the matching section slice to its parent on success.
 *
 * The form internally calls `useOrgProfileUpdateApi()` so consumers
 * typically don't need to do anything in `onSaved` other than
 * closing a modal or refetching. Keeping it as a callback (rather
 * than baking the API call into the form itself) lets the
 * financing drawers continue to wrap the same form and just
 * intercept the success event.
 */
export type OrgProfileSectionSavedHandler = (
  patch: Pick<OrgProfileUpdate, 'displayName' | 'logo' | 'legal' | 'bank'>,
  next: OrganizationProfile | undefined,
) => void;
